import React, { useEffect, useState } from "react";
import Modal from "../utils/Modal/Modal";
import {
  CategoryContainer,
  CategoryItem,
  CategoryText,
  Content,
  Header,
  IconsContainer,
  IconWrapper,
  ModalContainer,
  Tab,
  TabsContainer,
  TabText,
  Title,
  TitleContainer,
} from "./GenerateCollectionModal.styles";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import { ReactComponent as WhiteFavoriteIcon } from "../../assets/icons/whiteFavorites.svg";
import { ReactComponent as FilledFavoriteIcon } from "../../assets/icons/filledFavorites.svg";
import { ReactComponent as ImageIcon } from "../../assets/icons/image.svg";
import { ReactComponent as VideoRecorderIcon } from "../../assets/icons/video-recorder.svg";
import { useDispatch, useSelector } from "react-redux";
import { selectUser } from "../../store/slices/user/slice";
import {
  selectGenerateCollection,
  selectRegenerateCollectionPending,
  selectSelectedCollection,
  selectToggleToFavoriteLoading,
} from "../../store/slices/model/slice";
import { toggleToFavorite } from "../../store/slices/model/asyncThunks";
import ExtendVideo from "../ExtendVideo/ExtendVideo";
import ImageToVideo from "../ImageToVideo/ImageToVideo";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { formatModelName } from "../../utils/utils";
import { useLocation, useSearchParams } from "react-router-dom";
import { NUDIFY_MODEL_NAME } from "../../utils/constants";

const MODAL_CONTENT = {
  IMAGE_TO_VIDEO: "IMAGE_TO_VIDEO",
  EXTEND_VIDEO: "EXTEND_VIDEO",
};

const GenerateCollectionModal = ({ isOpen, onClose, onChangeFilter }) => {
  useLockBodyScroll(isOpen);
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useSelector(selectUser);
  const collection = useSelector(selectSelectedCollection);
  const loading = useSelector(selectToggleToFavoriteLoading);
  const { loading: generateLoading } = useSelector(selectGenerateCollection);
  const regenerateLoading = useSelector(selectRegenerateCollectionPending);

  const isLoading = loading || generateLoading || regenerateLoading;

  const [selectedTab, setSelectedTab] = useState(MODAL_CONTENT.IMAGE_TO_VIDEO);

  const location = useLocation();
  const isExplorePage = location.pathname === "/";

  const handleFavoriteToggle = () => {
    if (!isLoading && collection._id) {
      dispatch(toggleToFavorite({ collectionId: collection._id }));
    }
  };

  useEffect(() => {
    if (collection) {
      if (collection.input.video.url) {
        setSelectedTab(MODAL_CONTENT.EXTEND_VIDEO);
      } else {
        setSelectedTab(MODAL_CONTENT.IMAGE_TO_VIDEO);
      }
    }
  }, [collection]);

  if (!user || !collection) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!isLoading) {
          onClose();
        }
      }}
    >
      <ModalContainer>
        <Header>
          <TitleContainer>
            <Title>{formatModelName(collection.name)}</Title>
            <IconWrapper onClick={handleFavoriteToggle}>
              {user.favoriteCollections.includes(collection._id) ? (
                <FilledFavoriteIcon />
              ) : (
                <WhiteFavoriteIcon />
              )}
            </IconWrapper>
          </TitleContainer>
          <IconsContainer>
            <IconWrapper
              onClick={() => {
                if (!isLoading) {
                  onClose();
                }
              }}
            >
              <CloseIcon />
            </IconWrapper>
          </IconsContainer>
        </Header>

        <CategoryContainer>
          {isExplorePage &&
            !!collection.categories &&
            collection.categories.map((category, index) => {
              return (
                <CategoryItem
                  key={category.categoryId}
                  onClick={() => {
                    searchParams.set("page", 1);
                    setSearchParams(searchParams);

                    if (onChangeFilter) {
                      onChangeFilter(category.categoryId);
                      onClose();
                    }
                  }}
                >
                  <CategoryText>{category.name}</CategoryText>
                </CategoryItem>
              );
            })}
        </CategoryContainer>

        {collection?.modelName !== NUDIFY_MODEL_NAME && (
          <TabsContainer>
            <Tab
              onClick={() => {
                if (!isLoading) {
                  setSelectedTab(MODAL_CONTENT.IMAGE_TO_VIDEO);
                }
              }}
              $selected={selectedTab === MODAL_CONTENT.IMAGE_TO_VIDEO}
            >
              <ImageIcon
                color={
                  selectedTab === MODAL_CONTENT.IMAGE_TO_VIDEO
                    ? "#18181B"
                    : "#fff"
                }
              />
              <TabText $selected={selectedTab === MODAL_CONTENT.IMAGE_TO_VIDEO}>
                Image to{" "}
                {collection?.modelName === NUDIFY_MODEL_NAME
                  ? "Image"
                  : "Video"}
              </TabText>
            </Tab>
            <Tab
              $disabled={collection?.modelName === NUDIFY_MODEL_NAME}
              onClick={() => {
                if (!isLoading) {
                  setSelectedTab(MODAL_CONTENT.EXTEND_VIDEO);
                }
              }}
              $selected={selectedTab === MODAL_CONTENT.EXTEND_VIDEO}
            >
              <VideoRecorderIcon
                color={
                  selectedTab === MODAL_CONTENT.EXTEND_VIDEO
                    ? "#18181B"
                    : "#fff"
                }
              />
              <TabText $selected={selectedTab === MODAL_CONTENT.EXTEND_VIDEO}>
                Extend Video
              </TabText>
            </Tab>
          </TabsContainer>
        )}
        <Content>
          {selectedTab === MODAL_CONTENT.EXTEND_VIDEO ? (
            <ExtendVideo onClose={onClose} />
          ) : (
            <ImageToVideo onClose={onClose} />
          )}
        </Content>
      </ModalContainer>
    </Modal>
  );
};

export default GenerateCollectionModal;
